import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  OnboardingState,
  DEFAULT_ONBOARDING_STATE,
  getOnboardingState,
  setOnboardingState,
  markOnboardingSkipped as markSkipped,
  markOnboardingCompleted as markCompleted,
} from '@/lib/onboarding-state';

export function useOnboarding() {
  const navigate = useNavigate();
  const [state, setState] = useState<OnboardingState>(() => {
    return getOnboardingState() || DEFAULT_ONBOARDING_STATE;
  });

  const updateState = useCallback((updates: Partial<OnboardingState>) => {
    setState((prev) => {
      const newState = { ...prev, ...updates };
      setOnboardingState(newState);
      return newState;
    });
  }, []);

  const goToStep = useCallback(
    (step: 1 | 2 | 3 | 4) => {
      updateState({ currentStep: step });
    },
    [updateState]
  );

  const completeStep = useCallback((step: number) => {
    setState((prev) => {
      const completedSteps = prev.completedSteps.includes(step)
        ? prev.completedSteps
        : [...prev.completedSteps, step];
      const newState = { ...prev, completedSteps };
      setOnboardingState(newState);
      return newState;
    });
  }, []);

  const skipOnboarding = useCallback(() => {
    markSkipped();
    setState((prev) => ({ ...prev, skippedAt: new Date().toISOString() }));
    navigate('/app');
  }, [navigate]);

  const setFormId = useCallback(
    (formId: string, formName: string) => {
      updateState({ formId, formName });
    },
    [updateState]
  );

  const markTelegramConnected = useCallback(() => {
    updateState({ telegramConnected: true });
  }, [updateState]);

  const markTestSubmitted = useCallback(() => {
    updateState({ testSubmitted: true });
  }, [updateState]);

  const markEmailConfigured = useCallback(() => {
    updateState({ emailConfigured: true });
  }, [updateState]);

  const finishOnboarding = useCallback(() => {
    markCompleted();
    setState((prev) => ({ ...prev, currentStep: 'completed' }));
  }, []);

  const resetOnboarding = useCallback(() => {
    const newState = DEFAULT_ONBOARDING_STATE;
    setOnboardingState(newState);
    setState(newState);
  }, []);

  return {
    state,
    goToStep,
    completeStep,
    skipOnboarding,
    setFormId,
    markTelegramConnected,
    markTestSubmitted,
    markEmailConfigured,
    finishOnboarding,
    resetOnboarding,
  };
}
